import * as THREE from 'three';
import type { Course } from '../course/Course';
import { isPointInRegion } from '../utils/geometry';
import type { BallState } from './BallState';

export interface HazardResult {
  type: 'water';
  penaltyStrokes: number;
  dropPosition: THREE.Vector3;
  message: string;
}

export class HazardDetector {
  private static readonly WATER_ENTRY_CLEARANCE_METERS = 0.09;

  constructor(private readonly course: Course) {}

  isWater(point: THREE.Vector3): boolean {
    return this.course.waterFeatures.some((water) =>
      isPointInRegion({ x: point.x, z: point.z }, water.def.region),
    );
  }

  isWaterEntry(point: THREE.Vector3, terrainHeight: number): boolean {
    return this.course.waterFeatures.some((water) => {
      if (!isPointInRegion({ x: point.x, z: point.z }, water.def.region)) return false;
      const effectiveWaterLevel = Math.max(water.def.waterLevel, terrainHeight + 0.045);
      return point.y <= effectiveWaterLevel + HazardDetector.WATER_ENTRY_CLEARANCE_METERS;
    });
  }
}

export class DropPositionResolver {
  constructor(private readonly detector: HazardDetector) {}

  resolve(lastSafePosition: THREE.Vector3, fallback: THREE.Vector3): THREE.Vector3 {
    if (!this.detector.isWater(lastSafePosition)) return lastSafePosition.clone();
    const direction = fallback.clone().sub(lastSafePosition).setY(0);
    if (direction.lengthSq() < 0.001) direction.set(1, 0, 0);
    direction.normalize();
    for (let i = 1; i <= 20; i++) {
      const candidate = fallback.clone().addScaledVector(direction, -i * 2);
      if (!this.detector.isWater(candidate)) return candidate;
    }
    return fallback.clone();
  }
}

export class WaterHazardResolver {
  private inHazard = false;
  private readonly drops: DropPositionResolver;

  constructor(private readonly detector: HazardDetector) {
    this.drops = new DropPositionResolver(detector);
  }

  check(
    state: BallState,
    lastSafePosition: THREE.Vector3,
    terrainHeightAtBall: number,
  ): HazardResult | null {
    if (this.inHazard || !this.detector.isWaterEntry(state.position, terrainHeightAtBall))
      return null;
    this.inHazard = true;
    const dropPosition = this.drops.resolve(lastSafePosition, state.position);
    state.velocity.set(0, 0, 0);
    state.spin.set(0, 0, 0);
    state.position.copy(dropPosition);
    state.isResting = true;
    state.phase = 'resting';
    return { type: 'water', penaltyStrokes: 1, dropPosition, message: 'Water hazard — 1 stroke' };
  }

  reset(): void {
    this.inHazard = false;
  }
}
